import type {
  ProjectAssetArchiveReason,
  ProjectAssetDuplicateFailureReason,
  ProjectAssetDuplicateProposalStatus,
  ProjectInquiryBlocker,
  ProjectInquiryGroupStatus,
  ProjectInquiryListMode,
  ProjectInquiryStatus,
  ProjectRegisterApproveBlocker,
  ProjectRegisterMergeRefusal,
  ProjectRoomHolderLicenceState,
} from 'models/projects/projectEnums';
import type { ProjectExternalAuditStep, ProjectRoomTodo } from 'models/projects/project';
import type ProjectRoom from 'models/projects/projectRoom';
import type {
  ProjectRiskPrecheck,
  ProjectRoomInquirySignal,
  ProjectRoomItem,
  ProjectRoomRelated,
} from 'models/projects/projectRoom';
import type { RiskMethod } from 'models/risk';

//
// The parts of a room read: what only some rooms know. A room read carries one part of the kind of the read
// (`ProjectRoom.part`), and each row one part of the kind of the row (`ProjectRoomItem.part`); `$kind` names the kind.
// Every member of a part is present; a room or row that knows nothing beyond the common fields has no part. Read a
// part through getRoomPart, getItemPart or getRowsWithPart, which narrow it to its kind. The room is replaced as a
// whole on every read, so the parts need no isEqual.
//

/** A person a room names. */
export interface PersonRef {
  /** Guid; undefined for an entry without a user. */
  userId?: string;

  name: string;
}

/** One role's asset inquiry, the same in every room that names it. Before the send: status notSent, no task. */
export interface ProjectInquiry {
  /** The role, a Guid. */
  roleId: string;

  roleName: string;

  /** Undefined before the send; the task a window opens, never where the inquiry stands (`status` says that). */
  taskId?: number;

  /** Where the inquiry stands, the server's: not sent, sent, opened, late (overdue) or completed (answered). */
  status: ProjectInquiryStatus;

  /** The responsible person: the task's assignee; undefined before the send. */
  assignee?: PersonRef;

  /** The task's owner, who follows the inquiry up; undefined before the send. */
  askedBy?: PersonRef;

  sentAt?: Date;

  deadline?: Date;

  /** Undefined while the task is not completed. */
  completedAt?: Date;

  /** The bedrijfsmiddelen on the role's list, in use and not in use. */
  lineCount: number;

  /** Of `lineCount`, the lines with an answer. */
  answeredCount: number;

  /** Once completed: the lines without an answer (the server's count); 0 before. */
  unansweredCount: number;

  /**
   * False when the person the inquiry is with no longer holds the role (the role changed hands after the send); true
   * otherwise, also before the send.
   */
  assigneeIsHolder: boolean;
}

/**
 * One group of an inquiry's list in the asset answers room: a container of the asset tree, the role's own or the primary
 * container of the role's other assets, with the lines whose primary container it is; or Overig, the lines added
 * without a group that still have no container.
 */
export interface ProjectInquiryListGroup {
  /** The container's AssetId; undefined for Overig. */
  containerId?: number;

  /** The container's name; for Overig its localized name, undefined when not sent (getAssetInquiryGroupName). */
  name?: string;

  /** The container's own description as plain text, the help of the group; undefined without one. */
  description?: string;

  /** The container's AssetType. */
  assetType: number;

  /** The lines of the group, ordered like the items. */
  itemIds: string[];

  /**
   * Where the group stands for the caller (the server's rule): nothing to confirm, a line or its own container still
   * waits for Klopt, volgende, or everything answered.
   */
  status: ProjectInquiryGroupStatus;

  /**
   * The group's container is an asset of the list's role (a group the role owns, also without lines): confirmGroup
   * answers it too, and the server's status counts it.
   */
  containerOwnedByRole: boolean;

  /** The container's comment trail. */
  commentTrailId: number;

  /** The container's audit trail. */
  auditTrailId: number;
}

/** One inquiry's list in the asset answers room: the assignee's messages, and its groups with their lines. */
export interface ProjectInquiryList {
  /** Undefined on the list of a role without a task (a read with roleId), which is read-only. */
  taskId?: number;

  /** What the caller may do on this list: every decision, only an add (the register approved), or nothing (the server's rule). */
  mode: ProjectInquiryListMode;

  /** The responsible person's messages on the task, oldest first. */
  signals: ProjectRoomInquirySignal[];

  /** In the order of the asset tree, the group without a container last. */
  groups: ProjectInquiryListGroup[];

  /** The outcome inquiry of the list's role is sent already: the completion does not announce it as still to come. */
  outcomeInquirySent: boolean;
}

/** What a send of the asset inquiry or the outcome inquiry promises the responsible person. */
export interface ProjectInquirySendTerms {
  /** The days a responsible person gets to answer an inquiry. */
  responseDays: number;

  /** The deadline an inquiry task sent at the time of the read gets. */
  deadline: Date;
}

/** One asset inquiry the asset register waits on. */
export interface ProjectRoomRegisterWait {
  roleName: string;

  /** False while the inquiry is not sent. */
  sent: boolean;

  /** The assignee of the inquiry: who has it while it is sent. */
  person?: PersonRef;

  /** The deadline of the inquiry while it is sent. */
  deadline?: Date;
}

//
// The room parts, one kind per room read.
//

/** The risks room: whether the set-aside route can run. */
export interface ProjectRisksRoomPart {
  $kind: 'risks';

  precheck: ProjectRiskPrecheck;
}

/**
 * What the caller's approve does in a room whose documents the directie approves: they are the directie (`self`), they
 * record the approval for the directie (`onBehalf`), or they cannot approve (`none`).
 */
export const projectKickoffApproveModes = ['self', 'onBehalf', 'none'] as const;
export type ProjectKickoffApproveMode = (typeof projectKickoffApproveModes)[number];

/**
 * Why the caller cannot approve yet: nobody holds the Directie role (`noDirectie`), several holders can take the work
 * (`severalDirectie`), none of the holders can (`directieCannotTake`), the caller is neither the directie nor the
 * Security Officer (`notAllowed`), or the caller is one of them but cannot change the project (`noWriteAccess`).
 */
export const projectKickoffApproveBlocks = [
  'noDirectie',
  'severalDirectie',
  'directieCannotTake',
  'notAllowed',
  'noWriteAccess',
] as const;
export type ProjectKickoffApproveBlock = (typeof projectKickoffApproveBlocks)[number];

/** The scope room: whether the scope documents cover every standard of the project. */
export interface ProjectScopeRoomPart {
  $kind: 'scope';

  documentsComplete: boolean;
}

/**
 * The documents room, whose documents (the basis documents and the scope documents) the directie approves with "Goedkeuren":
 * what the caller's approve does, and who the directie is.
 */
export interface ProjectDocumentsRoomPart {
  $kind: 'documentsRoom';

  approveMode: ProjectKickoffApproveMode;

  /** Why the caller cannot approve yet; undefined when approving is possible. */
  approveBlock?: ProjectKickoffApproveBlock;

  /** The directie holder who can take the work; undefined when no single holder can. */
  directieUserId?: string;

  /** The display name of directieUserId; undefined without one. */
  directieName?: string;

  /** The name of the Directie role as the organization shows it in Rollen; undefined without a Directie role. */
  directieRoleName?: string;
}

/** What every room that shows inquiries knows. */
interface ProjectInquiryRoomPartBase {
  /** Whether the caller may give an open inquiry to someone else: an admin, or whoever manages the project. */
  callerMayReassign: boolean;
}

/** The asset inquiry: what a send promises. */
export interface ProjectAssetInquiryRoomPart extends ProjectInquiryRoomPartBase {
  $kind: 'assetInquiry';

  send: ProjectInquirySendTerms;

  /** The roles without their inquiry, counted by why (the server's to-do). */
  todo: ProjectRoomTodo[];
}

/** The outcome inquiry: what a send promises, whether the organization holds the catalogue, and the controls nobody answers for. */
export interface ProjectOutcomeInquiryRoomPart extends ProjectInquiryRoomPartBase {
  $kind: 'outcomeInquiry';

  send: ProjectInquirySendTerms;

  /** The roles without their inquiry, counted by why (the server's to-do). */
  todo: ProjectRoomTodo[];

  /** A control of the project's packages carries a criterion with an ExternalId. */
  hasCatalogue: boolean;

  /** The controls with such criteria and no role that answers them, by code: their questions go to nobody. */
  unownedControls: ProjectUnownedControl[];
}

/** The asset answers: the inquiries the caller sees, their lists by task, and the register's approval. */
export interface ProjectAssetAnswersRoomPart extends ProjectInquiryRoomPartBase {
  $kind: 'assetAnswers';

  /** Undefined while the register is not approved. */
  registerApprovedAt?: Date;

  inquiries: ProjectInquiry[];

  lists: ProjectInquiryList[];
}

/** The outcome answers: the inquiries the caller sees and their lists. */
export interface ProjectOutcomeAnswersRoomPart extends ProjectInquiryRoomPartBase {
  $kind: 'outcomeAnswers';

  inquiries: ProjectInquiry[];

  lists: ProjectOutcomeList[];
}

/** The inquiry progress room. */
export interface ProjectInquiryProgressRoomPart extends ProjectInquiryRoomPartBase {
  $kind: 'inquiryProgress';

  /** The inquiries that ask for whoever manages the project: late, a holder to choose, not sent (the server's to-do). */
  todo: ProjectRoomTodo[];
}

/** The external audit room: what was done for the audit, whether it may be confirmed, and what an undo takes back. */
export interface ProjectExternalAuditRoomPart {
  $kind: 'externalAudit';

  state: ProjectExternalAuditStep;
}

/**
 * The asset register: its approval, every role with a list, what it waits on, what is still to assign, and whether it
 * can be approved.
 */
export interface ProjectAssetRegisterRoomPart {
  $kind: 'assetRegister';

  /** Undefined while the register is not approved. */
  approvedAt?: Date;

  /** Every role with a list, sent or not. */
  inquiries: ProjectInquiry[];

  /** The asset inquiries the register waits on before its approval. Empty once each is completed or late. */
  waitingOn: ProjectRoomRegisterWait[];

  /** Until when it waits on the inquiries that are sent: the latest deadline, when each has one. */
  lockedUntil?: Date;

  /** The assets in Nog toe te wijzen. */
  toAssign: number;

  /** Register goedkeuren can go now (the server's rule). */
  canApprove: boolean;

  /** Why it cannot be approved now, in the server's order; a value this client cannot name is left out. */
  approveBlockers: ProjectRegisterApproveBlocker[];

  /** The duplicate search. */
  duplicates: ProjectAssetDuplicates;

  /** The roles a line can get as owner (assignRole): the project's roles, as the server says. */
  ownerRoleIds: string[];
}

/**
 * The asset register's duplicate search: whether it runs for the organisation (the AI add-on and its AI on, and the
 * register not approved), whether it checked the register, why the last search stored no result, and the proposals the
 * caller may see.
 */
export interface ProjectAssetDuplicates {
  available: boolean;

  /** No AI add-on: the register says where to read about it. */
  addOnMissing: boolean;

  /** A search has stored a result. */
  checked: boolean;

  /** Why the last search stored no result; undefined when it did. */
  failure?: ProjectAssetDuplicateFailure;

  openCount: number;

  /** Open ones first, then the resolved ones; a status this client cannot name is left out. */
  proposals: ProjectAssetDuplicateProposal[];
}

/** Why the last duplicate search stored no result: the reason, the assets in use and out of use, and the server's maximum. */
export interface ProjectAssetDuplicateFailure {
  reason: ProjectAssetDuplicateFailureReason;

  /** The assets in use (the failed search's count after tooMany). */
  inUseCount: number;

  /** The assets out of use the search also takes, because the asset inquiry set them to not in use. */
  outOfUseCount: number;

  /** The most assets one search takes, in use and out of use together. */
  max: number;
}

/** One group of lines the duplicate search proposed as the same thing. */
export interface ProjectAssetDuplicateProposal {
  /** duplicates:{proposalId}: the item of keepApart and undoKeepApart. */
  itemId: string;

  /** The Guid of the proposal: a merge's proposalId. */
  proposalId: string;

  status: ProjectAssetDuplicateProposalStatus;

  members: ProjectAssetDuplicateMember[];

  /** The member that stays on a merge of every member; undefined when blocked or resolved. */
  staysItemId?: string;

  /** Two or more members are linked: they are not merged in the register. */
  blocked: boolean;

  /** A member changed since the search: a merge from it is refused. */
  stale: boolean;

  suggestedNameItemId?: string;

  /** Why the AI assistant holds them to be the same, in the organisation language. */
  reason?: string;

  resolvedBy?: PersonRef;

  resolvedOn?: Date;
}

/**
 * A member of a proposal and, while it is open, what links it elsewhere and whether the asset inquiry set it to not in
 * use (a contradiction: a merge brings it back into use).
 */
export interface ProjectAssetDuplicateMember {
  itemId: string;

  /** Another record uses it, or a classification or a tag is on it. */
  linked: boolean;

  fromPackage: boolean;

  hasExternalId: boolean;

  /** The not-in-use answer of the asset inquiry that took it out of use; undefined for a member in use. */
  outOfUse?: ProjectAssetLineAnswer;

  /** The role whose inquiry gave outOfUse. */
  outOfUseRoleName?: string;
}

/** The part of a room read, one kind per room read. */
export type ProjectRoomPart =
  | ProjectRisksRoomPart
  | ProjectScopeRoomPart
  | ProjectDocumentsRoomPart
  | ProjectAssetInquiryRoomPart
  | ProjectOutcomeInquiryRoomPart
  | ProjectAssetAnswersRoomPart
  | ProjectOutcomeAnswersRoomPart
  | ProjectAssetRegisterRoomPart
  | ProjectInquiryProgressRoomPart
  | ProjectExternalAuditRoomPart;

export type ProjectRoomPartKind = ProjectRoomPart['$kind'];

export type ProjectRoomPartOf<K extends ProjectRoomPartKind> = Extract<ProjectRoomPart, { $kind: K }>;

//
// The answer on an asset line, one kind per answer.
//

/** What every answer on an asset line knows. */
interface ProjectAssetAnswerBase {
  /** Who gave the answer; undefined without a name to show. */
  by?: PersonRef;

  /** When the answer was given; undefined for an answer the window shows before the server has it (a pending write). */
  on?: Date;
}

/** The asset is in use as it is. */
export interface ProjectAssetKeepAnswer extends ProjectAssetAnswerBase {
  $kind: 'keep';
}

/** The asset is in use under another name or in another container. */
export interface ProjectAssetAdjustAnswer extends ProjectAssetAnswerBase {
  $kind: 'adjust';

  /** The line's name before the first adjust of the answer's inquiry; undefined when the name did not change. */
  previousName?: string;

  /** The line's primary container before the first adjust of the answer's inquiry; undefined without a move. */
  previousContainerId?: number;
}

/** The asset is not in use. */
export interface ProjectAssetNotInUseAnswer extends ProjectAssetAnswerBase {
  $kind: 'notInUse';

  /** Why (Hebben we niet, Gebruiken we niet meer); undefined for a value this client cannot name. */
  reason?: ProjectAssetArchiveReason;
}

/** The responsible person added the asset in the inquiry. */
export interface ProjectAssetAddedAnswer extends ProjectAssetAnswerBase {
  $kind: 'added';
}

/** The answer on an asset line. */
export type ProjectAssetLineAnswer =
  | ProjectAssetKeepAnswer
  | ProjectAssetAdjustAnswer
  | ProjectAssetNotInUseAnswer
  | ProjectAssetAddedAnswer;

//
// The row parts, one kind per kind of row.
//

/** One person who holds a role. */
export interface ProjectRoomHolder extends PersonRef {
  /** Whether the person can take the role's work; undefined for a wire value this client cannot name. */
  licenceState?: ProjectRoomHolderLicenceState;
}

/** A row of the roles room: the people who hold the role; empty when nobody does. */
export interface ProjectRoleRowPart {
  $kind: 'role';

  holders: ProjectRoomHolder[];
}

/** A row of the risks room: the risk's content. */
export interface ProjectRiskRowPart {
  $kind: 'risk';

  /** The affected asset(s), html; empty without one. */
  asset: string;

  /** The consequence, html; empty without one. */
  analysis: string;

  /** The treatment plan, html; empty without one. */
  strategy: string;

  /** Undefined for a value this client cannot name. */
  method?: RiskMethod;

  /** The controls that treat this risk. */
  controls: ProjectRoomRelated[];
}

/** One bedrijfsmiddel on a role's list in the asset inquiry, read-only. */
export interface ProjectRoomInquiryAsset {
  assetId: number;

  name: string;
}

/** One group of a role's list in the asset inquiry: the lines in use whose primary container is one asset. */
export interface ProjectRoomAssetListGroup {
  /** The container's AssetId; undefined for the group without a container. */
  containerId?: number;

  /** The container's name; undefined for the group without a container (getAssetInquiryGroupName words it). */
  name?: string;

  assetCount: number;

  /** Ordered by name. */
  assets: ProjectRoomInquiryAsset[];
}

/** What a role's row in either send room knows: its holders, its inquiry, and whether and to whom a send goes. */
interface ProjectInquiryRoleRowPartBase {
  /** The people who hold the role; empty when nobody does. */
  holders: ProjectRoomHolder[];

  inquiry: ProjectInquiry;

  /** Why the inquiry cannot be sent to this role; undefined when it can, and when it was sent. */
  blocker?: ProjectInquiryBlocker;

  /** Who a send now gives it to without a choice, while it is not sent; undefined when it needs a choice or stands. */
  sendTo?: PersonRef;
}

/** A role's row in the asset inquiry: also what is on its list, per group in the order of the asset tree. */
export interface ProjectAssetInquiryRowPart extends ProjectInquiryRoleRowPartBase {
  $kind: 'assetInquiryRole';

  /** The number of bedrijfsmiddelen on the role's list. */
  assetCount: number;

  groups: ProjectRoomAssetListGroup[];
}

/** A role's row in the outcome inquiry. */
export interface ProjectOutcomeInquiryRowPart extends ProjectInquiryRoleRowPartBase {
  $kind: 'outcomeInquiryRole';
}

/** A role's row in either send room. */
export type ProjectInquiryRoleRowPart = ProjectAssetInquiryRowPart | ProjectOutcomeInquiryRowPart;

/** What every asset line knows: where it is and its owner role. */
interface ProjectAssetLinePartBase {
  /** The line's primary container (AssetId), when the caller may see it. */
  containerId?: number;

  /** The line's owner role, a Guid; undefined without one. */
  ownerRoleId?: string;
}

/** A line of the asset answers: the list it is on, its answer, and what the caller may do. */
export interface ProjectAnswerLinePart extends ProjectAssetLinePartBase {
  $kind: 'answerLine';

  /** The inquiry task whose list the line is on. */
  inquiryTaskId: number;

  /** Undefined without an answer, and for an answer this client cannot name. Its `on` is sent back as expectedModifiedOn. */
  answer?: ProjectAssetLineAnswer;

  /** The caller added the line in this inquiry and may take it back: only while the list takes every decision. */
  removable: boolean;

  /**
   * The caller may change the line: any line but one another person added, and every line for whoever manages the
   * project; only while the list takes every decision.
   */
  editable: boolean;

  /** A forgotten bedrijfsmiddel added after the register's approval. */
  lateAddition: boolean;
}

/** A line of the asset inquiry's preview: read-only, without an answer. */
export interface ProjectPreviewLinePart extends ProjectAssetLinePartBase {
  $kind: 'previewLine';
}

/** Who added an asset line in an inquiry, and when. */
export interface ProjectAssetAddition {
  by?: PersonRef;

  at?: Date;
}

/** A line of the asset register: its answer, its owner role, that role's inquiry, and whether it is to assign. */
export interface ProjectRegisterLinePart extends ProjectAssetLinePartBase {
  $kind: 'registerLine';

  /** The newest answer of an inquiry of the owner role, or the added of whoever added it; undefined without one. */
  answer?: ProjectAssetLineAnswer;

  /** A forgotten bedrijfsmiddel added after the register's approval. */
  lateAddition: boolean;

  /** The owner role, a Guid, while it is a role of the project. */
  projectRoleId?: string;

  projectRoleName?: string;

  /** The owner role's asset inquiry task, once sent. */
  inquiryTaskId?: number;

  /** In use, on the list of a sent inquiry, without an answer of it. */
  unanswered: boolean;

  /** In Nog toe te wijzen: in use, and no owner role of the project. */
  toAssign: boolean;

  /** The asset is a group of its owner role's own on that role's inquiry list, also without lines: a move target. */
  ownedGroup: boolean;

  /** The asset's type (the AssetType enum). */
  assetType: number;

  /**
   * In use, no container, and added without a group in an asset inquiry of the project (Overig on its list): it holds up
   * the approval until a move puts it in a group.
   */
  addedWithoutGroup: boolean;

  /** Who added the line in an inquiry, and when; undefined for any other line. */
  added?: ProjectAssetAddition;

  /** The open proposal of the duplicate search the line is in (duplicates:{proposalId}). */
  duplicateProposalId?: string;

  /** The line this line was merged into, with its name when the caller may see it. */
  mergedInto?: ProjectAssetMergedInto;
}

/** The line a merged line went into, who merged and when, and why "Toch apart houden" (unmerge) is refused now. */
export interface ProjectAssetMergedInto {
  itemId: string;

  name?: string;

  mergedOn?: Date;

  mergedBy?: PersonRef;

  /** Why an unmerge is refused now; undefined when it can be applied. */
  unmergeBlocker?: ProjectRegisterMergeRefusal;
}

//
// The Uitvraag uitkomsten (outcome-inquiry and outcome-answers). An outcome row is an audit criterion of a control the
// project's packages installed; its item id is the criterion's ExternalId (such as UC-TG-02), a document row
// `doc:{linkId}` or `doc:none`, the ownership row `ownership:{roleId}`. A closed value this client cannot name is
// dropped.
//

/**
 * The answers of an outcome, the same three for every row (`answers`, empty for a row archived
 * after the send and for one answered elsewhere). The answer is not stored: the server reads it
 * from the criterion's evidence task; unsure ("Ik weet het niet") is written as notYet with a mark
 * on the criterion.
 */
export const projectOutcomeAnswers = ['alreadySo', 'notYet', 'unsure'] as const;
export type ProjectOutcomeAnswer = (typeof projectOutcomeAnswers)[number];

export const projectDocumentChoices = ['adopt', 'discuss'] as const;
export type ProjectDocumentChoice = (typeof projectDocumentChoices)[number];

/**
 * Why nobody answers the questions of a control: noOwner (no role answers them and no role owns the control),
 * roleMissing (a criterion names an answer role the organization no longer has).
 */
export const projectUnownedControlReasons = ['noOwner', 'roleMissing'] as const;
export type ProjectUnownedControlReason = (typeof projectUnownedControlReasons)[number];

/** A wire string narrowed to one of these values; undefined for an absent or unknown one. */
export function parseProjectOneOf<T extends string | number>(
  values: readonly T[],
  value: string | number | null | undefined,
): T | undefined {
  return values.find((candidate) => candidate === value);
}

/** A control whose questions nobody answers, and why. */
export interface ProjectUnownedControl {
  code: string;

  name: string;

  /** Undefined for a reason this client cannot name. */
  reason?: ProjectUnownedControlReason;
}

/** One document group of an outcome list. */
export interface ProjectOutcomeGroup {
  documentItemId: string;

  /** The outcome rows, in catalogue order; empty for a document the role owns without outcomes. */
  itemIds: string[];
}

/** One role's outcome inquiry list. */
export interface ProjectOutcomeList {
  /** Undefined on the list of a role without a task (a read with roleId), which is read-only. */
  taskId?: number;

  roleId: string;

  /** The caller answers this list: its assignee, while the task is not rejected or archived. */
  writable: boolean;

  signals: ProjectRoomInquirySignal[];

  groups: ProjectOutcomeGroup[];

  /** The ownership question of the list; undefined when the role owns nothing, so there is nothing to accept. */
  ownershipItemId?: string;
}

/** The answer on an outcome. */
export interface ProjectOutcomeAnswerValue {
  answer?: ProjectOutcomeAnswer;
}

/** The answer an archived version of the criterion had, before its text changed (`previousAnswer`). */
export interface ProjectOutcomePreviousAnswer {
  answer?: ProjectOutcomeAnswer;

  at?: Date;
}

/** A question row of the outcome answers: the criterion, how it is answered, and its answer. */
export interface ProjectQuestionRowPart {
  $kind: 'question';

  code: string;

  /** Undefined in a preview: no task exists before the send. */
  inquiryTaskId?: number;

  documentItemId?: string;

  /** The criterion row; 0 when the read sends none. Comments about the question go on its trail. */
  auditCriterionId: number;

  commentTrailId: number;

  auditTrailId: number;

  /** The criterion's state (auditCriterionState*: Active, NotApplicable, Archived). */
  state?: string;

  /** OneTime or Recurring (auditCriterionMode*); informational, the answer binds a one-time task either way. */
  mode?: string;

  /** The task bound to the criterion: the evidence task of Ja, dit is al zo, or a task outside the inquiry. */
  taskId?: number;

  /** The bound task is completed or archived. */
  taskClosed: boolean;

  /** The answer before the criterion's text changed; undefined when it did not change, or had no answer. */
  previousAnswer?: ProjectOutcomePreviousAnswer;

  /** The comments on the criterion's trail; 0 for a reader who may not open the question. */
  commentCount: number;

  /** The menu, in order. */
  answers: ProjectOutcomeAnswer[];

  /** Undefined without an answer. */
  answer?: ProjectOutcomeAnswerValue;

  /** Answered. */
  complete: boolean;

  /** The organization archived the outcome after the send: no menu, counted done. */
  notApplicableHere: boolean;

  /** The files given as evidence, oldest first; empty unless the answer is a yes answer. */
  evidence: ProjectRoomOutcomeEvidence[];
}

/** A file given as evidence for an outcome. */
export interface ProjectRoomOutcomeEvidence {
  linkId: number;

  name: string;

  url?: string;

  /** The evidence task the file is on (the task bound to the outcome's criterion); absent for a file not saved yet. */
  evidenceTaskId?: number;
}

/** The owner of a document. */
export interface ProjectRoomOwner {
  roleName?: string;

  people: PersonRef[];
}

/** A document row of the outcome answers: its owner, whether the caller chooses, and the choice. */
export interface ProjectDocumentRowPart {
  $kind: 'document';

  linkId?: number;

  /** Undefined in a preview: no task exists before the send. */
  inquiryTaskId?: number;

  owner?: ProjectRoomOwner;

  choosable: boolean;

  choice?: ProjectDocumentChoice;

  reason?: string;

  complete: boolean;
}

/** One item a role owns: a control, a risk or a library document (its link id, without a code). */
export interface ProjectRoomOwnedItem {
  id: number;

  code?: string;

  name: string;
}

/** The ownership row of the outcome answers: what the role owns. */
export interface ProjectOwnershipRowPart {
  $kind: 'ownership';

  roleId: string;

  roleName: string;

  /** Undefined in a preview: no task exists before the send. */
  inquiryTaskId?: number;

  controls: ProjectRoomOwnedItem[];

  risks: ProjectRoomOwnedItem[];

  documents: ProjectRoomOwnedItem[];
}

//
// Voortgang verantwoordelijken (inquiry-progress): one row per responsible person, item id the user id, or
// `role:{roleId}` for a role without one responsible person (no `person`). Each inquiry of the person, of both kinds.
//

/** A document the person wants to discuss on the policy day. */
export interface ProjectInquiryDiscuss {
  linkId: number;

  name: string;

  reason?: string;
}

/**
 * A question of the person's outcome list answered "Ik weet het niet": it goes to the policy day.
 */
export interface ProjectInquiryUnsure {
  code: string;

  name: string;
}

/** One inquiry of a responsible person in the inquiry progress room. */
export interface ProjectInquiryProgress {
  /** The room it is sent from: asset-inquiry or outcome-inquiry. */
  roomKey: string;

  inquiry: ProjectInquiry;

  /** Before the send: why it cannot be sent yet. */
  blocker?: ProjectInquiryBlocker;

  /** The messages, oldest first; empty unless the reader owns the inquiry task. */
  messages: ProjectRoomInquirySignal[];

  discuss: ProjectInquiryDiscuss[];

  /** Outcome inquiry: the questions answered "Ik weet het niet". */
  unsure: ProjectInquiryUnsure[];
}

/** A row of the inquiry progress room. */
export interface ProjectInquiryProgressRowPart {
  $kind: 'inquiryProgress';

  /** Undefined for a role without one responsible person. */
  person?: PersonRef;

  inquiries: ProjectInquiryProgress[];
}

/** A row of the documents room: who recorded the approval of the document for the directie. */
export interface ProjectKickoffDocumentPart {
  $kind: 'kickoffDocument';

  /** Undefined when the document is not approved, or the directie holder approved it themselves. */
  recordedById?: string;
}

/** The part of a row, one kind per kind of row. */
export type ProjectRoomItemPart =
  | ProjectRoleRowPart
  | ProjectRiskRowPart
  | ProjectAssetInquiryRowPart
  | ProjectOutcomeInquiryRowPart
  | ProjectAnswerLinePart
  | ProjectPreviewLinePart
  | ProjectRegisterLinePart
  | ProjectQuestionRowPart
  | ProjectDocumentRowPart
  | ProjectOwnershipRowPart
  | ProjectInquiryProgressRowPart
  | ProjectKickoffDocumentPart;

export type ProjectRoomItemPartKind = ProjectRoomItemPart['$kind'];

export type ProjectRoomItemPartOf<K extends ProjectRoomItemPartKind> = Extract<ProjectRoomItemPart, { $kind: K }>;

//
// Reading a part: narrowed to its kind, so no reader casts.
//

const isRoomPart = <K extends ProjectRoomPartKind>(
  part: ProjectRoomPart | undefined,
  kind: K,
): part is ProjectRoomPartOf<K> => part?.$kind === kind;

/** The room's part of that kind; undefined for a read of another room, or a room without a part. */
export function getRoomPart<K extends ProjectRoomPartKind>(
  room: ProjectRoom | undefined,
  kind: K,
): ProjectRoomPartOf<K> | undefined {
  const part = room?.part;

  return isRoomPart(part, kind) ? part : undefined;
}

const isItemPart = <K extends ProjectRoomItemPartKind>(
  part: ProjectRoomItemPart | undefined,
  kind: K,
): part is ProjectRoomItemPartOf<K> => part?.$kind === kind;

/** The row's part of that kind; undefined for a row of another kind. */
export function getItemPart<K extends ProjectRoomItemPartKind>(
  item: ProjectRoomItem,
  kind: K,
): ProjectRoomItemPartOf<K> | undefined {
  const part = item.part;

  return isItemPart(part, kind) ? part : undefined;
}

/** The rows that carry a part of that kind, each with its part. */
export function getRowsWithPart<K extends ProjectRoomItemPartKind>(
  items: ProjectRoomItem[],
  kind: K,
): { item: ProjectRoomItem; part: ProjectRoomItemPartOf<K> }[] {
  return items.flatMap((item) => {
    const part = getItemPart(item, kind);

    return part === undefined ? [] : [{ item: item, part: part }];
  });
}

/** Whether the caller may give an open inquiry to someone else; false for a room that shows no inquiries. */
export const getCallerMayReassign = (room: ProjectRoom | undefined): boolean => {
  const part = room?.part;
  switch (part?.$kind) {
    case 'assetInquiry':
    case 'outcomeInquiry':
    case 'assetAnswers':
    case 'outcomeAnswers':
    case 'inquiryProgress':
      return part.callerMayReassign;
    default:
      return false;
  }
};

/** A role's row part in either send room; undefined for any other row. */
export const getInquiryRoleRowPart = (item: ProjectRoomItem): ProjectInquiryRoleRowPart | undefined => {
  const part = item.part;
  switch (part?.$kind) {
    case 'assetInquiryRole':
    case 'outcomeInquiryRole':
      return part;
    default:
      return undefined;
  }
};

/** The asset line part of a line of the answers room, its preview or the register; undefined for any other row. */
export const getAssetLinePart = (
  item: ProjectRoomItem,
): ProjectAnswerLinePart | ProjectPreviewLinePart | ProjectRegisterLinePart | undefined => {
  const part = item.part;
  switch (part?.$kind) {
    case 'answerLine':
    case 'previewLine':
    case 'registerLine':
      return part;
    default:
      return undefined;
  }
};

/** The answer on an asset line, of either line kind that has one; undefined without one. */
export const getAssetAnswer = (item: ProjectRoomItem): ProjectAssetLineAnswer | undefined => {
  const part = item.part;
  switch (part?.$kind) {
    case 'answerLine':
    case 'registerLine':
      return part.answer;
    default:
      return undefined;
  }
};

/** Who added an asset line in an inquiry: the person of the answer added; undefined for any other line. */
export const getAssetLineAddedBy = (item: ProjectRoomItem): PersonRef | undefined => {
  const answer = getAssetAnswer(item);

  return answer?.$kind === 'added' ? answer.by : undefined;
};

/** A person's name to show; undefined without the person or with an empty name, so the texts without a name are used. */
export const getPersonName = (person: PersonRef | undefined): string | undefined =>
  person !== undefined && person.name.length > 0 ? person.name : undefined;

/** Whether two people are the same one: the same user id, or the same name. */
export const isSamePerson = (left: PersonRef | undefined, right: PersonRef | undefined): boolean =>
  left !== undefined &&
  right !== undefined &&
  ((left.userId !== undefined && left.userId === right.userId) || (left.name.length > 0 && left.name === right.name));

/**
 * Who added an asset line, seen in an inquiry whose responsible person is `assignee`: the one rule of the Opgegeven door
 * cell in the register's window and in the inquiry's list. When the line's `addedBy` is that person (isSamePerson, so
 * also by user id alone) it is them, `self`; else `addedBy` when it has a name; undefined when the line names nobody.
 */
export const getAssetLineAdder = (
  addedBy: PersonRef | undefined,
  assignee: PersonRef | undefined,
): { person: PersonRef; self: boolean } | undefined => {
  if (isSamePerson(addedBy, assignee)) {
    const person = getPersonName(assignee) !== undefined ? assignee : addedBy;
    if (person !== undefined) return { person: person, self: true };
  }

  return addedBy !== undefined && getPersonName(addedBy) !== undefined ? { person: addedBy, self: false } : undefined;
};

/** An inquiry not sent: no task, no person, nothing counted. */
export const getEmptyProjectInquiry = (roleId: string, roleName: string): ProjectInquiry => ({
  roleId: roleId,
  roleName: roleName,
  status: 'notSent',
  lineCount: 0,
  answeredCount: 0,
  unansweredCount: 0,
  assigneeIsHolder: true,
});

/** The inquiry task a send room's row opens: the sent task; undefined before the send (the row opens its preview). */
export const getInquiryTaskIdToOpen = (item: ProjectRoomItem): number | undefined =>
  getInquiryRoleRowPart(item)?.inquiry.taskId;
